import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTranslation } from 'react-i18next';

import { ErrorDialog } from 'library/components/controls/dialogs/ErrorDialog';
import { FileIcon } from 'library/components/controls/files/FileIcon';
import {
	ALLOWED_FILE_EXTENSIONS_STRING,
	MAX_FILE_LIMIT,
	MAX_FILE_SIZE_MB,
} from 'library/components/controls/files/fileUploadValidation';
import { Sxable } from 'library/types';

import { FilteredList } from './Types';

interface FileWarningDialogProps extends Sxable {
	amount?: number;
	extensions?: string;
	filteredFiles: FilteredList;
	onClose: () => void;
	open?: boolean;
	size?: number;
}

export const FileWarningDialog = ({
	amount,
	extensions,
	filteredFiles,
	onClose,
	open = true,
	size,
	sx,
}: FileWarningDialogProps) => {
	const { t } = useTranslation('component');

	return (
		<ErrorDialog
			title={t('upload-files-error-dialog-title')}
			onClose={onClose}
			open={open}
			sx={sx}>
			<List>
				<WarningFileSection
					text={t('upload-files-error-dialog-subtitle-duplicates')}
					files={filteredFiles['AlreadyExists']}
				/>
				<WarningFileSection
					text={t('upload-files-error-dialog-subtitle-size-limit', {
						size: size || MAX_FILE_SIZE_MB,
					})}
					files={filteredFiles['TooBig']}
				/>
				<WarningFileSection
					text={t('upload-files-error-dialog-subtitle-extensions', {
						extensions:
							extensions || ALLOWED_FILE_EXTENSIONS_STRING,
					})}
					files={filteredFiles['WrongExtension']}
				/>
				<WarningFileSection
					text={t('upload-files-error-dialog-subtitle-count-limit', {
						amount: amount || MAX_FILE_LIMIT,
					})}
					files={filteredFiles['TooMany']}
				/>
			</List>
		</ErrorDialog>
	);
};

export const WarningFileSection = ({
	text,
	files,
}: {
	text: string;
	files: File[];
}): React.JSX.Element => {
	if (files.length === 0) {
		return <></>;
	}
	return (
		<>
			<ListItemText
				key={text}
				primary={text}
				slotProps={{
					primary: {
						sx: { fontWeight: 'bold' },
					},
				}}></ListItemText>
			{files.map((file) => (
				<ListItem key={file.name}>
					<ListItemIcon>
						<FileIcon mimeType={file.type} color="secondary" />
					</ListItemIcon>
					<ListItemText primary={file.name} />
				</ListItem>
			))}
		</>
	);
};
